import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000')
    .replace(/\/+$/, '')
    .replace(/\/api$/, '');

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('ceviri_token'));
    const [loading, setLoading] = useState(true);

    // Token varsa kullanıcı bilgisini yükle
    const loadUser = useCallback(async (authToken) => {
        if (!authToken) { setLoading(false); return; }
        try {
            const res = await fetch(`${API_BASE}/api/auth/me`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUser(data);
            } else {
                // Token geçersiz
                localStorage.removeItem('ceviri_token');
                setToken(null);
                setUser(null);
            }
        } catch (err) {
            console.error('Kullanıcı yüklenemedi:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUser(token);
    }, [token, loadUser]);

    const login = async (email, password) => {
        const res = await fetch(`${API_BASE}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });
        const data = await res.json();
        if (!res.ok) throw data;
        localStorage.setItem('ceviri_token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        return data;
    };

    const loginWithGoogle = async (access_token) => {
        const res = await fetch(`${API_BASE}/api/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ access_token })
        });
        const data = await res.json();
        if (!res.ok) throw data;
        localStorage.setItem('ceviri_token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        return data;
    };

    const register = async ({ first_name, last_name, email, password }) => {
        const res = await fetch(`${API_BASE}/api/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ first_name, last_name, email, password })
        });
        const data = await res.json();
        if (!res.ok) throw data;
        return data;
    };

    const logout = () => {
        localStorage.removeItem('ceviri_token');
        setToken(null);
        setUser(null);
    };

    const resendVerification = async (email) => {
        const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        const data = await res.json();
        if (!res.ok) throw data;
        return data;
    };

    const refreshUser = useCallback(() => {
        return loadUser(token);
    }, [token, loadUser]);

    return (
        <AuthContext.Provider value={{ user, token, loading, login, loginWithGoogle, register, logout, resendVerification, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};

export default AuthContext;
